import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
    AccountActions,
    AccountSelectors,
    AccountService,
    ModulesSelectors,
    ModalsService,
    DefaultModalConfig,
    LocalizationSelectors
} from '@serviceos-ng/core';
import { from, Observable, Subscription } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import { PaymentsComponent } from '../../payments/components/payments/payments.component';
import { PaymentsCardConfig } from '../payment-card';

@Component({
    selector: 'serviceos-web-payment-form',
    templateUrl: './payment-form.component.html',
    styleUrls: ['./payment-form.component.scss'],
})
export class PaymentFormComponent implements OnInit, OnDestroy {
    @Select(AccountSelectors.paymethods)
    public paymethods!: Observable<
        | Array<{
              id: number;
              sort: number;
              default: boolean;
              description: string;
              label: string;
              brand: string;
              payment_method_id: number;
          }>
        | null
        | []
    >;

    @Select(
        ModulesSelectors.getComponentFromModule(
            'account',
            'account_settings_screen.payments'
        )
    )
    paymentsCardConfig$!: Observable<PaymentsCardConfig | null>;

    @Select(AccountSelectors.paymentMethods)
    public paymentMethods!: Observable<Array<any> | null | []>;

    public subscriptions: Subscription[] = [];
    public paymethodFormExpanded = false;
    public showLoader = false;
    private selectedPaymentMethod: any;
    public paymethodSetup: any = null;
    public selectedTypeOfPayment: 'Stripe' | 'Braintree' | null = null;
    private paymentMethodsList: Array<string> = [
        'Stripe',
        'Braintree',
        'Apple',
    ];
    public hasPaymentMethods = true;
    private confirmDeleteModalConfig: any = null;

    public formErrorMessage = '';

    public documentDirection = '';

    @ViewChild(PaymentsComponent)
    private paymentsComponent: PaymentsComponent | null = null;

    constructor(
        private _store: Store,
        private _accountService: AccountService,
        private _modalsService: ModalsService
    ) { }

    ngOnInit(): void {

        this._store.select(LocalizationSelectors.getCurrentDirection()).subscribe(
            (currentDirection) => {
                if (currentDirection) {
                    this.documentDirection = currentDirection;
                }
            }
        )

        this.confirmDeleteModalConfig = this._store.selectSnapshot(ModulesSelectors.getComponentFromModule('account', 'confirm_delete_modal'));

        this.subscriptions.push(
            this.paymentMethods
                .pipe(
                    tap((paymentMethods) => {
                        if (paymentMethods && paymentMethods.length) {
                            const filteredPaymentMethods: Array<any> = paymentMethods.filter(
                                (paymentMethod: any) => paymentMethod.default
                            );

                            // set the default payment method or first one as selected
                            this.selectedPaymentMethod = filteredPaymentMethods.length
                                ? filteredPaymentMethods[0]
                                : paymentMethods[0];

                            this.hasPaymentMethods = true;
                        } else {
                            // hide 'Add card' button
                            this.hasPaymentMethods = false;
                        }
                    })
                )
                .subscribe()
        );
    }

    /**
     * Delete paymethod handler
     * @param addressId
     */
    public deletePaymethod(paymethodId: number): void {
        const dialogRef = this._modalsService.open(
            new DefaultModalConfig(
                '', 
                this.confirmDeleteModalConfig.title,
                [
                    {
                        title: this.confirmDeleteModalConfig.cancel_button_title,
                        type: 'close',
                    },
                    {
                        title: this.confirmDeleteModalConfig.confirm_button_title,
                        type: 'confirm',
                    }
                ],
                'warning'
            )
        );
        dialogRef.close$.pipe(take(1)).subscribe((res) => {
            if (res === 'confirm') {
                this.showLoader = true;

                this._store
                    .dispatch(new AccountActions.DeletePaymethod({ id: paymethodId }))
                    .pipe(
                        finalize(() => {
                            this.showLoader = false;
                        })
                    )
                    .subscribe(
                        (res) => {
                            this.formErrorMessage = '';
    
                        },
                        (error) => {
                            this.formErrorMessage = error?.error[0]?.message;
                        }
                    );
            }
        });
    }

    /**
     * Mark paymethod as default
     * @param paymethod
     */
    public setAsDefault(paymethod: any): void {
        this.showLoader = true;

        const payload: any = {
            id: paymethod.id,
            default: true,
        };

        this._store
            .dispatch(new AccountActions.MarkPaymethodAsDefault(payload))
            .pipe(
                finalize(() => {
                    this.showLoader = false;
                })
            )
            .subscribe((res) => {
                this.formErrorMessage = '';

            },
            (error) => {
                this.formErrorMessage = error?.error[0]?.message;
            });
    }

    /**
     * Handler for Add Card button click
     */
    public onAddNewCard(): void {
        this.showLoader = true;

        this._accountService
            .getPaymethodSetup({
                payment_method_id: this.selectedPaymentMethod.id,
            })
            .pipe(
                finalize(() => {
                    this.paymethodFormExpanded = true;
                    this.showLoader = false;
                })
            )
            .subscribe(
                (paymethodSetup: any) => {
                    this.paymethodSetup = paymethodSetup;

                    // load payment method component
                    this.paymentMethodsList.forEach(
                        (paymentMethodName: any) => {
                            if (
                                paymethodSetup &&
                                paymethodSetup.type &&
                                paymethodSetup.type.indexOf(
                                    paymentMethodName.toLowerCase()
                                ) !== -1
                            ) {
                                this.selectedTypeOfPayment = paymentMethodName;
                            }
                        }
                    );
                },
                () => {
                    // TODO!
                }
            );
    }

    /**
     * Payment form submit handler
     */
    public onPaymentFormSubmit(): void {
        this.showLoader = true;

        this.paymentsComponent?.generateToken().then(
            (paymentData) => {
                const requestData = {
                    payment_method_id: this.selectedPaymentMethod.id,
                    // paymethod: { this is needed for transaction!
                    setup: {
                        type: this.paymethodSetup.type,
                        payload: paymentData,
                    },
                    // }
                };

                this._store
                    .dispatch(new AccountActions.CreatePaymethod(requestData))
                    .pipe(
                        finalize(() => {
                            this.showLoader = false;
                            this.paymethodFormExpanded = false;
                        })
                    )
                    .subscribe();
            },
            (error) => {
                // Error is handled in payments.component
                this.showLoader = false;
            }
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.map((el) => el.unsubscribe());
    }
}
